/**
 * Animation catalog — Phase C MVP (2026-05-16).
 *
 * Single source of truth for per-layer effects. The editor's CSS animation
 * preview AND the future compose-time ffmpeg pipeline both read from here, so
 * a designer's preview always matches what the TV ends up playing.
 *
 * Three slot kinds:
 *   IN     — one-shot at layer's time_in (duration ~600 ms)
 *   STAY   — continuous loop between time_in and time_out (cycles ~1.5 s)
 *   OUT    — one-shot ending at layer's time_out (duration ~600 ms)
 *
 * Each entry exposes:
 *   { kind, label, cssClass, keyframes }
 * where `keyframes` is the raw @keyframes body (without the `@keyframes name {`
 * wrapper). injectAnimationStyles() converts these to a single global stylesheet.
 */

export const EFFECT_IN_DURATION  = 0.6;   // seconds
export const EFFECT_OUT_DURATION = 0.6;
export const EFFECT_STAY_CYCLE   = 1.5;

// 2026-05-17 — zoom-in / zoom-out removed (no reliable compose-time
// implementation). Stay effects pulse + glow-loop removed in favour of
// gentle-sway (x oscillation) — both bob (y) and sway (x) are cheap overlay
// expressions that render reliably in the saved MP4.
const IN_EFFECTS = {
  'none':            { label: 'None' },
  'fade-in':         { label: 'Fade in',         keyframes: '0% { opacity: 0 } 100% { opacity: var(--layer-opacity, 1) }' },
  'slide-in-left':   { label: 'Slide in ← left', keyframes: '0% { transform: translateX(-60px); opacity: 0 } 100% { transform: translateX(0); opacity: var(--layer-opacity, 1) }' },
  'slide-in-right':  { label: 'Slide in → right',keyframes: '0% { transform: translateX(60px);  opacity: 0 } 100% { transform: translateX(0); opacity: var(--layer-opacity, 1) }' },
};

const STAY_EFFECTS = {
  'none':         { label: 'None' },
  'gentle-bob':   { label: 'Bob (up/down)',    keyframes: '0%,100% { transform: translateY(0) } 50% { transform: translateY(-5px) }' },
  'gentle-sway':  { label: 'Sway (left/right)',keyframes: '0%,100% { transform: translateX(0) } 50% { transform: translateX(5px) }' },
};

const OUT_EFFECTS = {
  'none':            { label: 'None' },
  'fade-out':        { label: 'Fade out',         keyframes: '0% { opacity: var(--layer-opacity, 1) } 100% { opacity: 0 }' },
  'slide-out-left':  { label: 'Slide out ← left', keyframes: '0% { transform: translateX(0); opacity: var(--layer-opacity, 1) } 100% { transform: translateX(-60px); opacity: 0 }' },
  'slide-out-right': { label: 'Slide out → right',keyframes: '0% { transform: translateX(0); opacity: var(--layer-opacity, 1) } 100% { transform: translateX(60px);  opacity: 0 }' },
};

const cssId = (slot, name) => `pm-anim-${slot}-${name}`;

export const EFFECT_IN_OPTIONS    = Object.entries(IN_EFFECTS).map(([val, e]) => ({ value: val, label: e.label }));
export const EFFECT_STAY_OPTIONS  = Object.entries(STAY_EFFECTS).map(([val, e]) => ({ value: val, label: e.label }));
export const EFFECT_OUT_OPTIONS   = Object.entries(OUT_EFFECTS).map(([val, e]) => ({ value: val, label: e.label }));

let _injected = false;
/** Inject the @keyframes rules for every effect into the document once. */
export function injectAnimationStyles() {
  if (_injected) return;
  if (typeof document === 'undefined') return;
  _injected = true;
  const lines = [];
  const dump = (slot, dict) => {
    for (const [name, eff] of Object.entries(dict)) {
      if (name === 'none' || !eff.keyframes) continue;
      lines.push(`@keyframes ${cssId(slot, name)} { ${eff.keyframes} }`);
    }
  };
  dump('in',   IN_EFFECTS);
  dump('stay', STAY_EFFECTS);
  dump('out',  OUT_EFFECTS);
  const style = document.createElement('style');
  style.id = 'pm-anim-styles';
  style.textContent = lines.join('\n');
  document.head.appendChild(style);
}

/**
 * Compute the visual style for a layer at a specific playhead time.
 *
 * Returns { visible, opacity, animation } where animation is a CSS
 * `animation` shorthand string (or '' when no effect is active).
 *
 * Algorithm:
 *   1. hidden=true → visible=false (designer toggle, ignored at compose time)
 *   2. currentTime outside [time_in, time_out] → visible=false
 *   3. Otherwise, apply opacity_start→opacity_end linear interp across the window
 *   4. If currentTime is in the IN window (first `EFFECT_IN_DURATION` seconds) → in animation
 *      If currentTime is in the OUT window (last `EFFECT_OUT_DURATION` seconds) → out animation
 *      Otherwise → stay animation (looping)
 */
export function computeLayerAnim(layer, currentTime, slideDuration) {
  const tIn    = Number.isFinite(layer.time_in)  ? layer.time_in  : 0;
  const tOut   = Number.isFinite(layer.time_out) ? layer.time_out : (slideDuration || 9999);
  const hidden = !!layer.hidden;
  if (hidden) return { visible: false, opacity: 0, animation: '' };
  if (currentTime < tIn || currentTime >= tOut) return { visible: false, opacity: 0, animation: '' };

  const span = Math.max(0.001, tOut - tIn);
  const t01  = Math.max(0, Math.min(1, (currentTime - tIn) / span));
  const os   = Number.isFinite(layer.opacity_start) ? layer.opacity_start : 100;
  const oe   = Number.isFinite(layer.opacity_end)   ? layer.opacity_end   : 100;
  const opacityPct = os + (oe - os) * t01;

  const effIn   = layer.effect_in   || 'none';
  const effStay = layer.effect_stay || 'none';
  const effOut  = layer.effect_out  || 'none';

  // Phase = 'in' | 'out' | 'stay' based on where currentTime sits in the window
  const tFromIn   = currentTime - tIn;
  const tFromOut  = tOut - currentTime;
  let animation = '';
  if (effIn !== 'none' && tFromIn < EFFECT_IN_DURATION) {
    // negative animation-delay scrubs the keyframe to the right point
    animation = `${cssId('in', effIn)} ${EFFECT_IN_DURATION}s ease-out -${tFromIn.toFixed(3)}s 1 both`;
  } else if (effOut !== 'none' && tFromOut < EFFECT_OUT_DURATION) {
    const elapsed = EFFECT_OUT_DURATION - tFromOut;
    animation = `${cssId('out', effOut)} ${EFFECT_OUT_DURATION}s ease-in -${elapsed.toFixed(3)}s 1 both`;
  } else if (effStay !== 'none') {
    animation = `${cssId('stay', effStay)} ${EFFECT_STAY_CYCLE}s ease-in-out infinite`;
  }

  return { visible: true, opacity: Math.max(0, Math.min(100, opacityPct)) / 100, animation };
}

/**
 * Cheap detector — true when a layer has ANY animation property that would
 * make a static image slide need MP4 compose output (Phase D auto-upgrade).
 */
export function hasAnyAnimation(layer, slideDuration) {
  if (!layer) return false;
  if (layer.effect_in   && layer.effect_in   !== 'none') return true;
  if (layer.effect_stay && layer.effect_stay !== 'none') return true;
  if (layer.effect_out  && layer.effect_out  !== 'none') return true;
  const os = Number.isFinite(layer.opacity_start) ? layer.opacity_start : 100;
  const oe = Number.isFinite(layer.opacity_end)   ? layer.opacity_end   : 100;
  if (os !== oe) return true;
  const tIn  = Number.isFinite(layer.time_in)  ? layer.time_in  : 0;
  const tOut = Number.isFinite(layer.time_out) ? layer.time_out : (slideDuration || 0);
  if (tIn !== 0 || (slideDuration && tOut !== slideDuration)) return true;
  return false;
}

/** Slide-level summary — true if any zone or text layer has an animation. */
export function slideHasAnimations(slide) {
  if (!slide) return false;
  const dur = slide.duration_sec || 0;
  const zones = Array.isArray(slide.zones) ? slide.zones : [];
  const texts = Array.isArray(slide.text_layers) ? slide.text_layers : [];
  return zones.some(z => hasAnyAnimation(z, dur)) || texts.some(t => hasAnyAnimation(t, dur));
}
